import { useEffect, useState } from 'react';
import { notify } from '@/lib/notify';
import { useNavigate, useParams } from 'react-router-dom';
import { useFlags } from '@/stores/identity';
import { useRef } from 'react';
import {
  BANNER_COLOR_PALETTE,
  BANNER_SEVERITY_THEMES,
  autoTextColorForBg,
  createBanner,
  listAllBanners,
  updateBanner,
  uploadBannerImage,
} from '@/lib/banners';
import type { CSSProperties } from 'react';
import { listOrgsSummary } from '@/lib/admin';
import type {
  AdminOrgSummary,
  AppRole,
  Banner,
  BannerLayout,
  BannerSeverity,
  BannerTargetKind,
} from '@/lib/types';

/**
 * /admin/banners/new and /admin/banners/:id — compose / edit form.
 */
const SEVERITIES: BannerSeverity[] = ['info', 'warning', 'success', 'promo'];
const TARGET_KINDS: { value: BannerTargetKind; label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'specific_orgs', label: 'Specific organizations' },
  { value: 'specific_roles', label: 'Specific roles' },
];
const ROLES: AppRole[] = ['owner', 'manager', 'staff'];
const TIERS: { value: string; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];
const LAYOUTS: { value: BannerLayout; label: string; hint: string }[] = [
  { value: 'compact', label: 'Compact', hint: 'Single-row strip. Good for status updates.' },
  { value: 'media_left', label: 'Media left', hint: 'Image on the left, text in the middle, CTA on the right.' },
  { value: 'media_bg', label: 'Media background', hint: 'Image as a soft background. Good for promo.' },
];

function nowLocal(offsetMin = 0): string {
  const d = new Date(Date.now() + offsetMin * 60_000);
  // datetime-local needs YYYY-MM-DDTHH:MM (no seconds, no tz)
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function BannerEdit() {
  const flags = useFlags();
  // Once we've confirmed admin, latch it so a transient `flags === null`
  // (e.g. when the tab refocuses and Supabase refreshes the session) doesn't
  // unmount this form and wipe every unsaved field.
  const [confirmedAdmin, setConfirmedAdmin] = useState(false);
  useEffect(() => {
    if (flags?.is_platform_admin) setConfirmedAdmin(true);
  }, [flags]);
  const isAdmin = !!flags?.is_platform_admin || confirmedAdmin;
  const flagsLoaded = flags !== null;
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [severity, setSeverity] = useState<BannerSeverity>('info');
  const [startsAt, setStartsAt] = useState(nowLocal());
  const [endsAt, setEndsAt] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [targetKind, setTargetKind] = useState<BannerTargetKind>('all');
  const [targetOrgIds, setTargetOrgIds] = useState<string[]>([]);
  const [targetRoles, setTargetRoles] = useState<AppRole[]>([]);
  const [targetTiers, setTargetTiers] = useState<string[]>([]);
  const [ackRequired, setAckRequired] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [layout, setLayout] = useState<BannerLayout>('compact');
  const [customBgHex, setCustomBgHex] = useState<string>('');
  const [customTextHex, setCustomTextHex] = useState<string>('');
  const [orgs, setOrgs] = useState<AdminOrgSummary[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function onPickFile(file: File | null) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be 5 MB or smaller.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadBannerImage(file);
      setImageUrl(url);
      if (!imageAlt && file.name) {
        setImageAlt(file.name.replace(/\.[^.]+$/, '').slice(0, 80));
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    listOrgsSummary().then(setOrgs).catch(() => undefined);
    if (isEdit) {
      listAllBanners()
        .then((all) => {
          const found = all.find((b) => b.id === id);
          if (found) hydrate(found);
        })
        .catch((err) => setError((err as Error).message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, id]);

  function hydrate(b: Banner) {
    setTitle(b.title);
    setBodyMd(b.body_md);
    setSeverity(b.severity);
    setStartsAt(b.starts_at.slice(0, 16));
    setEndsAt(b.ends_at ? b.ends_at.slice(0, 16) : '');
    setCtaText(b.cta_text ?? '');
    setCtaUrl(b.cta_url ?? '');
    setTargetKind(b.target_kind);
    setTargetOrgIds(b.target_org_ids);
    setTargetRoles(b.target_roles);
    setTargetTiers(b.target_tiers ?? []);
    setAckRequired(b.acknowledgement_required);
    setCustomBgHex(b.custom_bg_hex ?? '');
    setCustomTextHex(b.custom_text_hex ?? '');
    setImageUrl(b.image_url ?? '');
    setImageAlt(b.image_alt ?? '');
    setLayout(b.layout);
  }

  // Only render the 'not admin' state when flags are LOADED and the user
  // is definitively not an admin. Otherwise keep the form mounted so transient
  // `flags === null` during session refresh doesn't wipe form state.
  if (flagsLoaded && !isAdmin) {
    return (
      <div className="container mx-auto py-8 px-4">
        <p className="text-sm text-muted-foreground">Platform admin only.</p>
      </div>
    );
  }

  function validate(): string | null {
    if (!title.trim()) return 'Title is required.';
    if (!bodyMd.trim()) return 'Body is required.';
    if (endsAt && endsAt <= startsAt) return 'End must be after start.';
    if ((ctaText && !ctaUrl) || (!ctaText && ctaUrl))
      return 'Provide both CTA text and URL, or neither.';
    if (targetKind === 'specific_orgs' && targetOrgIds.length === 0)
      return 'Select at least one organization.';
    if (targetKind === 'specific_roles' && targetRoles.length === 0)
      return 'Select at least one role.';
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        body_md: bodyMd.trim(),
        severity,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        cta_text: ctaText.trim() || null,
        cta_url: ctaUrl.trim() || null,
        target_kind: targetKind,
        target_org_ids: targetKind === 'specific_orgs' ? targetOrgIds : [],
        target_roles: targetKind === 'specific_roles' ? targetRoles : [],
        target_tiers: targetTiers,
        acknowledgement_required: ackRequired,
        custom_bg_hex: customBgHex.trim() || null,
        custom_text_hex: customTextHex.trim() || null,
        image_url: imageUrl.trim() || null,
        image_alt: imageAlt.trim() || null,
        layout,
      };
      if (isEdit && id) {
        await updateBanner(id, payload);
        notify.success('Banner updated');
      } else {
        await createBanner(payload);
        notify.success('Banner created');
      }
      navigate('/admin/banners', { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  const theme = BANNER_SEVERITY_THEMES[severity];
  const previewHasCustom = !!customBgHex && /^#[0-9a-fA-F]{6}$/.test(customBgHex);
  const previewFg = previewHasCustom
    ? (customTextHex && /^#[0-9a-fA-F]{6}$/.test(customTextHex) ? customTextHex : autoTextColorForBg(customBgHex))
    : undefined;
  const previewStyle: CSSProperties | undefined = previewHasCustom
    ? { background: customBgHex, color: previewFg } : undefined;
  const previewCtaStyle: CSSProperties | undefined = previewHasCustom
    ? { background: previewFg, color: customBgHex } : undefined;
  const previewWrapBg = previewHasCustom ? '' : theme.gradient;
  const previewWrapFg = previewHasCustom ? '' : theme.text;
  const previewCtaClass = previewHasCustom ? '' : theme.cta;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-1">
          <button
            onClick={() => navigate('/admin/banners')}
            className="text-xs text-muted-foreground hover:underline"
          >
            ← Back to banners
          </button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isEdit ? 'Edit banner' : 'New banner'}
          </h1>
        </div>

        {/* Live preview */}
        <div className="rounded-lg border border-border overflow-hidden bg-muted/10 p-3 space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground px-1">
            Preview
          </p>
          {layout === 'media_bg' && imageUrl ? (
            <div
              className={`${previewWrapBg} ${previewWrapFg} relative overflow-hidden rounded-xl shadow-lg ring-1 ${theme.ring}`}
              style={previewStyle}
            >
              <img
                src={imageUrl}
                alt={imageAlt}
                className="absolute inset-0 h-full w-full object-cover opacity-25"
              />
              <div className="relative px-5 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold drop-shadow-sm">
                    {title || 'Banner title…'}
                  </p>
                  {bodyMd && (
                    <p className="text-sm whitespace-pre-wrap opacity-95 drop-shadow-sm">
                      {bodyMd}
                    </p>
                  )}
                </div>
                <PreviewActions ctaText={ctaText} ctaUrl={ctaUrl} ctaClass={previewCtaClass} ctaStyle={previewCtaStyle} ackRequired={ackRequired} />
              </div>
            </div>
          ) : layout === 'media_left' && imageUrl ? (
            <div
              className={`${previewWrapBg} ${previewWrapFg} rounded-xl shadow-lg ring-1 ${theme.ring} px-4 py-3`}
              style={previewStyle}
            >
              <div className="flex flex-wrap items-center gap-4">
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className="h-16 w-16 rounded-lg object-cover bg-white/20 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold">{title || 'Banner title…'}</p>
                  {bodyMd && (
                    <p className="text-sm whitespace-pre-wrap opacity-95">{bodyMd}</p>
                  )}
                </div>
                <PreviewActions ctaText={ctaText} ctaUrl={ctaUrl} ctaClass={previewCtaClass} ctaStyle={previewCtaStyle} ackRequired={ackRequired} />
              </div>
            </div>
          ) : (
            <div
              className={`${previewWrapBg} ${previewWrapFg} rounded-lg shadow-sm ring-1 ${theme.ring} px-4 py-2.5`}
              style={previewStyle}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1 flex items-start gap-3">
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      className="h-9 w-9 rounded-md object-cover bg-white/20 shrink-0"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold leading-tight">
                      {title || 'Banner title…'}
                    </p>
                    {bodyMd && (
                      <p className="text-xs whitespace-pre-wrap opacity-95 mt-0.5">
                        {bodyMd}
                      </p>
                    )}
                  </div>
                </div>
                <PreviewActions ctaText={ctaText} ctaUrl={ctaUrl} ctaClass={previewCtaClass} ctaStyle={previewCtaStyle} ackRequired={ackRequired} />
              </div>
            </div>
          )}
        </div>

        {error && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <form
          onSubmit={onSubmit}
          className="space-y-5 rounded-lg border border-border bg-card p-6"
        >
          <Field label="Title">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              className={inputClass}
              placeholder="e.g. Scheduled maintenance Saturday 2am–4am ET"
            />
          </Field>

          <Field
            label="Body"
            hint="Plain text. Line breaks preserved. No markup yet — keep it short."
          >
            <textarea
              rows={3}
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              className={inputClass}
              placeholder="Brief message. Use the CTA below for action links."
            />
          </Field>

          <Field label="Severity">
            <div className="flex flex-wrap gap-2">
              {SEVERITIES.map((s) => {
                const t = BANNER_SEVERITY_THEMES[s];
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setSeverity(s)}
                    className={`rounded-full px-3 py-1 text-xs ${
                      severity === s
                        ? `${t.bg} ${t.text}`
                        : 'border border-border bg-background hover:bg-muted/40'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {severity === 'promo' && (
              <p className="text-xs text-muted-foreground">
                Promo banners respect each user's promo opt-out (CASL).
              </p>
            )}
          </Field>

          <Field
            label="Background color"
            hint="Pick a swatch or set a custom hex to override the severity color. Text color is auto-derived for contrast (override below if needed)."
          >
            <div className="flex flex-wrap items-center gap-2">
              {BANNER_COLOR_PALETTE.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  title={`${c.name} (${c.hex})`}
                  onClick={() => setCustomBgHex(c.hex)}
                  style={{ background: c.hex }}
                  className={`h-7 w-7 rounded-md ring-1 ring-border ${customBgHex.toLowerCase() === c.hex.toLowerCase() ? 'ring-2 ring-offset-2 ring-foreground' : ''}`}
                  aria-label={c.name}
                />
              ))}
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(customBgHex) ? customBgHex : '#1e3a8a'}
                onChange={(e) => setCustomBgHex(e.target.value)}
                className="h-7 w-9 rounded border border-border bg-transparent cursor-pointer"
                title="Custom color"
              />
              <input
                type="text"
                value={customBgHex}
                onChange={(e) => setCustomBgHex(e.target.value)}
                maxLength={7}
                placeholder="#1e3a8a"
                className={`${inputClass} w-28 font-mono text-xs`}
              />
              {customBgHex && (
                <button
                  type="button"
                  onClick={() => { setCustomBgHex(''); setCustomTextHex(''); }}
                  className="text-xs underline text-muted-foreground"
                >
                  Reset to severity default
                </button>
              )}
            </div>
          </Field>

          {customBgHex && (
            <Field
              label="Text color (optional)"
              hint={`Leave blank to auto-pick (${/^#[0-9a-fA-F]{6}$/.test(customBgHex) ? autoTextColorForBg(customBgHex) : 'auto'}) based on background contrast.`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(customTextHex) ? customTextHex : '#ffffff'}
                  onChange={(e) => setCustomTextHex(e.target.value)}
                  className="h-7 w-9 rounded border border-border bg-transparent cursor-pointer"
                />
                <input
                  type="text"
                  value={customTextHex}
                  onChange={(e) => setCustomTextHex(e.target.value)}
                  maxLength={7}
                  placeholder="#ffffff"
                  className={`${inputClass} w-28 font-mono text-xs`}
                />
              </div>
            </Field>
          )}

          <Field label="Layout">
            <div className="flex flex-wrap gap-2">
              {LAYOUTS.map((l) => (
                <button
                  type="button"
                  key={l.value}
                  onClick={() => setLayout(l.value)}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    layout === l.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border bg-background hover:bg-muted/40'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {LAYOUTS.find((l) => l.value === layout)?.hint}
            </p>
          </Field>

          <Field
            label="Image (optional)"
            hint="Upload from your computer or paste a public URL. Up to 5 MB. JPEG / PNG / WebP / GIF."
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              className="hidden"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted/40 disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : 'Upload image'}
              </button>
              <span className="text-xs text-muted-foreground">or</span>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className={`${inputClass} flex-1 min-w-[14rem]`}
                placeholder="https://example.com/banner.jpg"
              />
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => {
                    setImageUrl('');
                    setImageAlt('');
                  }}
                  className="rounded-md border border-border bg-background px-3 py-2 text-xs hover:bg-muted/40"
                >
                  Clear
                </button>
              )}
            </div>
            {imageUrl && (
              <div className="flex items-center gap-3 rounded-md bg-muted/40 p-2">
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className="h-12 w-12 rounded object-cover"
                />
                <span className="text-xs text-muted-foreground truncate">
                  {imageUrl}
                </span>
              </div>
            )}
          </Field>
          <Field label="Image alt text" hint="Used by screen readers (a11y).">
            <input
              type="text"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              className={inputClass}
              placeholder="Brief description (e.g. 'pharmacist holding a tablet')"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts">
              <input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Ends (optional)">
              <input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA text (optional)">
              <input
                type="text"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                className={inputClass}
                placeholder="Learn more"
              />
            </Field>
            <Field label="CTA URL (optional)">
              <input
                type="url"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                className={inputClass}
                placeholder="https://…"
              />
            </Field>
          </div>

          <Field label="Audience">
            <select
              value={targetKind}
              onChange={(e) => setTargetKind(e.target.value as BannerTargetKind)}
              className={inputClass}
            >
              {TARGET_KINDS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {targetKind === 'specific_orgs' && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Pick the organizations that should see this banner.
                </p>
                <div className="max-h-48 overflow-y-auto rounded-md border border-border p-2 space-y-1">
                  {orgs.map((o) => (
                    <label
                      key={o.organization_id}
                      className="flex items-center gap-2 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={targetOrgIds.includes(o.organization_id)}
                        onChange={(e) => {
                          setTargetOrgIds((cur) =>
                            e.target.checked
                              ? [...cur, o.organization_id]
                              : cur.filter((x) => x !== o.organization_id)
                          );
                        }}
                      />
                      {o.display_name}{' '}
                      <span className="text-muted-foreground">
                        ({o.status})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {targetKind === 'specific_roles' && (
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={targetRoles.includes(r)}
                      onChange={(e) => {
                        setTargetRoles((cur) =>
                          e.target.checked
                            ? [...cur, r]
                            : cur.filter((x) => x !== r)
                        );
                      }}
                    />
                    {r}
                  </label>
                ))}
              </div>
            )}
          </Field>

          <Field
            label="Tier filter (optional)"
            hint="Restrict to orgs on these tiers. Leave all unchecked to show on every tier (default)."
          >
            <div className="flex flex-wrap gap-2">
              {TIERS.map((t) => (
                <label key={t.value} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={targetTiers.includes(t.value)}
                    onChange={(e) => {
                      setTargetTiers((cur) =>
                        e.target.checked
                          ? [...cur, t.value]
                          : cur.filter((x) => x !== t.value)
                      );
                    }}
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </Field>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={ackRequired}
              onChange={(e) => setAckRequired(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border border-border"
            />
            <span>
              <strong>Require acknowledgement.</strong> The dismiss button
              becomes "I acknowledge" — useful for legal updates.
            </span>
          </label>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Save as draft'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/banners')}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-muted/40"
            >
              Cancel
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            New banners start as <strong>draft</strong>. Use the Publish button
            on the list page to make them live.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';

function PreviewActions({
  ctaText,
  ctaUrl,
  ctaClass,
  ctaStyle,
  ackRequired,
}: {
  ctaText: string;
  ctaUrl: string;
  ctaClass: string;
  ctaStyle?: CSSProperties;
  ackRequired: boolean;
}) {
  return (
    <div className="flex items-center gap-3 shrink-0">
      {ctaText && ctaUrl && (
        <span className={`${ctaClass} rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm`} style={ctaStyle}>
          {ctaText}
        </span>
      )}
      <span className="text-xs font-medium underline-offset-2">
        {ackRequired ? 'I acknowledge' : 'Dismiss'}
      </span>
    </div>
  );
}
